import {
  Button,
  H4,
  Icon,
  imageUrls,
  InputEmail,
  InputCheckbox,
  InputField,
} from "@sellar-io/components";
import type { SupplierOnboardingGetResponseType } from "@sellar-io/api-v2/types";
import { isValidEmail } from "@sellar-io/common";
import { useForm } from "@tanstack/react-form";
import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Alert } from "~/components/alert/Alert";
import { SpinnerLegacy } from "~/components";
import { LANDING, PRODUCT_LIST } from "~/constants/routes";
import { BusinessForm } from "~/forms/business/BusinessForm";
import {
  generateBusinessFormFieldValues,
  generateBusinessRequest,
  type BusinessFormFieldValues,
} from "~/forms/business/BusinessFormValues";
import { useSellarUser } from "~/hooks";
import { apiV2AssertError } from "~/hooks/api/v2/apiV2Request";
import { fetchBusinessListPage } from "~/hooks/api/v2/business/useBusinessList";
import { useFirebaseUser } from "~/hooks/useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "~/hooks/useSellarCurrentBusiness.shared";
import {
  useSupplierOnboarding,
  useSupplierOnboardingComplete,
} from "~/hooks/api/v2/supplierOnboarding/useSupplierOnboarding";
import { TermsAndConditionsCopy } from "~/pages/settings/termsAndConditions/TermsAndConditions";

const emptyAddress = {
  name: "",
  street: "",
  city: "",
  region: "",
  postalCode: "",
} as const;

export const SupplierOnboardingPage = ({
  hasAccess = true,
}: {
  hasAccess?: boolean;
}) => {
  const onboardingQuery = useSupplierOnboarding({ enabled: hasAccess });
  const { user: firebaseUser } = useFirebaseUser();
  const { business } = useSellarCurrentBusiness();
  const { changeBusiness } = useSellarUser();
  const navigate = useNavigate();
  const [isSwitchingBusiness, setIsSwitchingBusiness] = useState(false);
  const hasAttemptedBusinessSwitch = useRef(false);

  const onboardingErrorStatus = onboardingQuery.isError
    ? apiV2AssertError(onboardingQuery.error)?.response?.status
    : undefined;

  useEffect(() => {
    if (
      !firebaseUser ||
      hasAttemptedBusinessSwitch.current ||
      (hasAccess && onboardingErrorStatus !== 403)
    ) {
      return;
    }

    hasAttemptedBusinessSwitch.current = true;
    const switchToAccessibleBusiness = async () => {
      setIsSwitchingBusiness(true);
      try {
        const businesses = await fetchBusinessListPage({
          user: firebaseUser,
          business: null,
          params: {
            query: [
              {
                type: "hasMembership",
                modifier: "EQUALS",
                value: true,
              },
            ],
            sort: [
              { direction: "asc", type: "type" },
              { direction: "asc", type: "name" },
            ],
            limit: 100,
          },
        });
        const fallbackBusiness = businesses.find(
          ({ id }) => id !== business?.id,
        );
        if (!fallbackBusiness) {
          setIsSwitchingBusiness(false);
          return;
        }

        await changeBusiness(fallbackBusiness.id);
        navigate(LANDING, { replace: true });
      } catch {
        setIsSwitchingBusiness(false);
      }
    };

    switchToAccessibleBusiness().catch(() => {
      setIsSwitchingBusiness(false);
    });
  }, [
    business?.id,
    changeBusiness,
    firebaseUser,
    hasAccess,
    navigate,
    onboardingErrorStatus,
  ]);

  if (onboardingQuery.isLoading || isSwitchingBusiness) {
    return <SpinnerLegacy />;
  }
  if (!onboardingQuery.data) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <Alert type="error" message="Unable to load supplier onboarding." />
      </div>
    );
  }

  return <SupplierOnboardingForm business={onboardingQuery.data} />;
};

const SupplierOnboardingForm = ({
  business,
}: {
  business: SupplierOnboardingGetResponseType;
}) => {
  const formId = useId();
  const navigate = useNavigate();
  const { signOut } = useSellarUser();
  const completionMutation = useSupplierOnboardingComplete();
  const [teamEmailDraft, setTeamEmailDraft] = useState("");
  const [teamEmailError, setTeamEmailError] = useState<string | null>(null);
  const [failedInvitations, setFailedInvitations] = useState<string[]>([]);
  const [acceptedTermsAndConditions, setAcceptedTermsAndConditions] =
    useState(false);

  const generatedValues = generateBusinessFormFieldValues(business);
  const isIncomplete = business.Supplier?.onboardingCompletedAt === null;
  const defaultValues: BusinessFormFieldValues = {
    ...generatedValues,
    email: business.email,
    invoiceEmails: business.invoiceEmails.join(","),
    hasNoAwrsNumber: !business.awrsNumber,
    hasNoVatNumber: !business.vatNumber,
    teamEmails: [] as string[],
    addresses: isIncomplete
      ? {
          ...generatedValues.addresses,
          DeliveryAddress: {
            ...generatedValues.addresses.DeliveryAddress,
            ...emptyAddress,
          },
          InvoiceAddress: {
            ...generatedValues.addresses.InvoiceAddress,
            ...emptyAddress,
          },
          CollectionAddress: {
            ...generatedValues.addresses.CollectionAddress,
            ...emptyAddress,
          },
          isInvoiceAddressSame: true,
          isCollectionAddressSame: true,
        }
      : generatedValues.addresses,
    Supplier: generatedValues.Supplier
      ? {
          ...generatedValues.Supplier,
          vatPercentage: isIncomplete
            ? ("" as const)
            : generatedValues.Supplier.vatPercentage,
        }
      : null,
  };

  const form = useForm({
    defaultValues,
    onSubmit: ({ value }) => {
      if (!acceptedTermsAndConditions) return;
      const request = generateBusinessRequest(value, "supplier");
      if (request.type !== "supplier" || !request.entityType) return;
      completionMutation.mutate(
        {
          ...request,
          entityType: request.entityType,
          teamEmails: value.teamEmails ?? [],
          acceptedTermsAndConditions,
        },
        {
          onSuccess: async (response) => {
            if (response.failedInvitationEmails.length > 0) {
              setFailedInvitations(response.failedInvitationEmails);
              form.setFieldValue("teamEmails", response.failedInvitationEmails);
              return;
            }
            navigate(PRODUCT_LIST, { replace: true });
          },
        },
      );
    },
  });
  const addTeamEmail = () => {
    const normalizedEmail = teamEmailDraft.trim().toLowerCase();
    const currentEmails = form.getFieldValue("teamEmails") ?? [];
    if (!isValidEmail(normalizedEmail)) {
      setTeamEmailError("Enter a valid email address.");
      return;
    }
    if (normalizedEmail === business.email.trim().toLowerCase()) {
      setTeamEmailError("The primary owner is already included.");
      return;
    }
    if (currentEmails.includes(normalizedEmail)) {
      setTeamEmailError("This email has already been added.");
      return;
    }
    form.setFieldValue("teamEmails", [...currentEmails, normalizedEmail]);
    setTeamEmailDraft("");
    setTeamEmailError(null);
  };

  const mutationError = completionMutation.isError
    ? (apiV2AssertError(completionMutation.error)?.response?.data.message ??
      "Unable to complete supplier onboarding.")
    : null;

  return (
    <main className="min-h-full bg-neutral-100 px-4 py-8 dark:bg-zinc-950">
      <div className="mx-auto flex max-w-4xl flex-col gap-6">
        <header className="flex items-start justify-between gap-4 dark:border-zinc-700">
          <div>
            <img
              src={imageUrls.sellarLogo.purple.full}
              alt="Sellar"
              className="mb-4 h-8"
            />
            <div className="flex flex-col gap-1">
              <H4 className="font-semibold">Complete your supplier profile</H4>
              <p>
                Add your business information before entering your workspace.
              </p>
            </div>
          </div>
          <Button color="slim" onClick={() => signOut()}>
            Sign out
            <Icon icon="EXIT_TO_APP" />
          </Button>
        </header>

        <div className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-8 dark:border-zinc-800 dark:bg-zinc-900">
          {mutationError && <Alert type="error" message={mutationError} />}
          {failedInvitations.length > 0 && (
            <Alert
              type="warning"
              message={`Your profile is complete, but invitations failed for: ${failedInvitations.join(", ")}. Submit again to retry.`}
            />
          )}

          <BusinessForm
            form={form}
            formId={formId}
            type="supplier"
            lockEmail
            requireEntityType
          />

          <section className="mt-6 border-t border-zinc-200 pt-6 dark:border-zinc-700">
            <h2 className="text-lg font-semibold text-zinc-950 dark:text-white">
              Invite your team
            </h2>
            <p className="mb-4 text-sm text-zinc-600 dark:text-zinc-400">
              Optional. These people will receive standard user access.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <InputField
                className="flex-1"
                label="Team member email"
                note={teamEmailError ?? undefined}
              >
                <InputEmail
                  value={teamEmailDraft}
                  onChange={(event) => setTeamEmailDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      addTeamEmail();
                    }
                  }}
                  placeholder="teammate@example.com"
                />
              </InputField>
              <Button type="button" onClick={addTeamEmail}>
                Add
              </Button>
            </div>
            <form.Field name="teamEmails" mode="array">
              {(field) => (
                <div className="mt-3 flex flex-col gap-2">
                  {(field.state.value ?? []).map((email, index) => (
                    <div
                      key={email}
                      className="flex items-center justify-between rounded-lg bg-zinc-100 px-3 py-2 text-sm dark:bg-zinc-800"
                    >
                      <span>{email}</span>
                      <Button
                        type="button"
                        color="tertiary"
                        onClick={() => field.removeValue(index)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </form.Field>
          </section>

          <section className="mt-6 border-t border-zinc-200 pt-6 dark:border-zinc-700">
            <TermsAndConditionsCopy supplierName={business.name} />
            <label className="mt-4 flex items-start gap-2">
              <InputCheckbox
                className="mt-0.5 flex-none"
                checked={acceptedTermsAndConditions}
                onChange={(event) =>
                  setAcceptedTermsAndConditions(event.target.checked)
                }
              />
              <span>
                I agree to the terms and conditions and am legally authorised to
                do so on behalf of {business.name}.
              </span>
            </label>
          </section>

          <div className="mt-8 flex justify-end">
            <Button
              type="submit"
              form={formId}
              loading={completionMutation.isPending}
              disabled={!acceptedTermsAndConditions}
            >
              {failedInvitations.length > 0
                ? "Retry invitations"
                : "Complete onboarding"}
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
};
